import {
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { OrderForm } from '@classes/order-form';
import { Order, LogElement } from '@models/order';
import { Patient } from '@models/patient';
import { Store } from '@models/store';
import { ProductElement } from '@models/product';
import { Vendor } from '@models/vendor';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { OrderService } from '@services/order.service';
import { PatientService } from '@services/patient.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { ReturnOrderDialogComponent } from '@components/admin/patient/return-order-dialog/return-order-dialog.component';

@Component({
  selector: 'app-patient-order-details',
  templateUrl: './patient-order-details.component.html',
  styleUrls: ['./patient-order-details.component.css'],
})
export class PatientOrderDetailsComponent
  extends OrdersBaseComponent
  implements OnInit
{
  patient: Patient;
  orderFormGroup: FormGroup;
  loaded = false;
  isNewOrder = false;
  patientId: string;
  currentStore: Store;
  panelOpenStateFrameDetails = true;
  panelOpenStatePrescription = false;
  panelOpenStateLens = false;
  panelOpenStateRepair = false;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    private patientService: PatientService,
    private router: Router,
    private dialog: MatDialog
  ) {
    super(
      locale,
      auth,
      orderService,
      vendorOrderService,
      productService,
      storeService,
      cdRef,
      fb,
      route,
      notificationService
    );
  }

  ngOnInit(): void {
    this.getAuthorized();
    this.currentStore = this.auth.getStoreObject();
    this.auth.getStoreObjectMessage().subscribe((store: Store) => {
      this.currentStore = store;
    });
    this.route.paramMap
      .pipe(
        switchMap((params) => {
          this.patientId = params.get('id');
          const orderId = params.get('orderId');
          this.isNewOrder = orderId === 'new';

          return forkJoin({
            patient: this.patientService.getPatient(this.patientId),
            order: this.isNewOrder
              ? of(this.createNewOrder())
              : this.orderService.getOrder(orderId),
          });
        })
      )
      .subscribe(({ patient, order }) => {
        this.patient = patient;
        this.orderFormGroup = OrderForm.initOrderFormGroup(
          order,
          this.auth,
          this.storeOptions
        );
        this.orderFormGroup.get('products.0.duty').enable();
        this.loaded = true;
        this.cdRef.detectChanges();
      });
  }

  createNewOrder(): Order {
    const newProduct = new ProductElement();
    newProduct.quantity = 1;
    newProduct.frame.frameSource = 'PATIENT';
    newProduct.product.vendor = new Vendor();
    newProduct.product.vendor.images = null;
    newProduct.product.store = this.auth.getStoreObject();

    const newOrder = new Order();
    newOrder.patient = this.patientId;
    newOrder.status = '';
    newOrder.type = 'O';
    newOrder.date = this.transformDate(Date.now());
    newOrder.user = this.auth.currentUser._id;
    newOrder.store = this.auth.getStore();
    newOrder.products.push(newProduct);

    return newOrder;
  }

  onSave(destination: string): void {
    if (destination === 'RETURN') {
      this.returnOrder();
      return;
    }

    if (destination === 'RE-STYLE') {
      this.orderFormGroup.get('todo').setValue('RE-STYLE');
      this.persistOrder(this.orderFormGroup.get('products.0._id').value == '', 'SAVE');
      return;
    }

    this.orderFormGroup
      .get('todo')
      .setValue(destination == 'SEND TO STACK' ? 'ORDER' : destination);
    this.orderFormGroup
      .get('products.0.frame.frameSource')
      .setValue(
        destination == 'ORDERED ON APPROVAL' ? 'PATIENT APPROVAL' : 'PATIENT'
      );

    const newOrder = this.orderFormGroup.get('products.0._id').value == '';
    let status = this.getOrderStatusString(
      this.orderFormGroup.get('type').value,
      destination
    );

    if (destination == 'ADDED TO INVENTORY') {
      status = 'CANCELED';
      const invoiceNumber = this.orderFormGroup.get('invoiceNumber').value;
      this.orderFormGroup.get('invoiceNumber').setValue(invoiceNumber + '-C');

      this.productService
        .incrementProductInventoryQuantity(
          this.orderFormGroup.get('products.0.product').value,
          1
        )
        .subscribe((incrementProduct) => {
          if (!this.orderFormGroup.get('products.0.product._id').value) {
            this.orderFormGroup
              .get('products.0.product._id')
              .setValue(incrementProduct.product._id);

            this.vendorOrderService
              .addProductIdToVendorOrder(this.orderFormGroup.value)
              .subscribe(() => {});
          }
          this.orderFormGroup.get('status').setValue(status);
          this.persistOrder(newOrder, destination);
        });
      return;
    }

    if (destination != 'SAVE') {
      this.orderFormGroup.get('status').setValue(status);
    }
    this.persistOrder(newOrder, destination);
  }

  persistOrder(newOrder: boolean, destination: string): void {
    this.saveFrame(this.orderFormGroup, newOrder).subscribe(() => {
      this.isNewOrder = false;
      if (destination !== 'SAVE') {
        this.navigateBack();
      } else {
        this.notificationService.show('success', 'ORDER SAVED', 'OK');
      }
    });
  }

  getOrderStatusString(type, destination): string {
    const status = /^ORDERED/.test(destination) ? 'AWAITING ' : 'ORDER ';
    switch (type) {
      case 'O':
      case 'N':
        return status + 'FRAME';
      case 'A':
        return status + 'ACCESSORY';
      case 'F':
        return status + 'PART';
    }
  }

  navigateBack(): void {
    this.router.navigate(['/admin/patient', this.patientId]);
  }

  returnOrder(): void {
    const order: Order = this.orderFormGroup.value;
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '250px',
      data: { message: 'RETURN ORDER?', order },
    });

    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe(() => {
        let credit = dialogRef.componentInstance.credit;
        if (typeof credit === 'string') {
          credit = parseFloat(credit.replace(/,/g, ''));
        }

        const logItem = new LogElement();
        logItem.initials = order['initials'];
        logItem.user = this.auth.currentUser._id;
        logItem.item = 'RETURNED';
        logItem.notes = '';
        logItem.date = Date.now().toString();
        order.log.unshift(logItem);
        order.invoiceNumber = order.invoiceNumber + '-R';
        order.patient = this.patientId;

        this.orderService.returnOrder(order, credit).subscribe(() => {
          this.orderFormGroup.get('status').setValue('RETURNED');
          this.notificationService.show('success', 'ORDER RETURNED', 'OK');
          this.navigateBack();
        });
      });
  }
}
